import { useEffect, useRef } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { API } from "@/config";
import Home from "@/pages/Home";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PaymentStatus from "@/pages/PaymentStatus";
import Careers from "@/pages/Careers";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import Exams from "@/pages/Exams";
import TakeExam from "@/pages/TakeExam";
import CustomerLogin from "@/pages/CustomerLogin";
import CustomerAccount from "@/pages/CustomerAccount";

const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    axios
      .post(`${API}/auth/google-session`, {}, {
        headers: { "X-Session-ID": sessionId },
        withCredentials: true,
      })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        const dest = data.role === "admin" ? "/admin" : data.role === "employee" ? "/employee" : "/account";
        navigate(dest, { replace: true, state: { user: data } });
      })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
      Signing you in...
    </div>
  );
};

const ProtectedRoute = ({ children, roles, loginPath = "/admin/login" }) => {
  const { user } = useAuth();
  const location = useLocation();
  const passedUser = location.state?.user;
  const current = passedUser || user;
  if (!passedUser) {
    if (user === null) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
          Loading...
        </div>
      );
    }
    if (user === false) return <Navigate to={loginPath} replace />;
  }
  if (roles && current && !roles.includes(current.role)) {
    const dest = current.role === "admin" ? "/admin" : current.role === "employee" ? "/employee" : "/account";
    return <Navigate to={dest} replace />;
  }
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment/status" element={<PaymentStatus />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/exams" element={<Exams />} />
      <Route path="/exams/:examId" element={<TakeExam />} />
      <Route path="/account/login" element={<CustomerLogin />} />
      <Route
        path="/account"
        element={
          <ProtectedRoute roles={["user"]} loginPath="/account/login">
            <CustomerAccount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee"
        element={
          <ProtectedRoute roles={["employee"]}>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.toggle("dark", localStorage.getItem("theme") !== "light");
  }, []);
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster richColors position="top-center" />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
